/**
 * Deletion Risk Assessment Service
 *
 * Evaluates risk level for account deletion requests based on:
 * 1. Session age - How long ago did the user authenticate?
 * 2. Device/browser change - Is this a new device?
 * 3. Account age - Is this a newly created account?
 *
 * Risk Levels:
 * - LOW: Same device, recent session, normal account age → Google re-auth + typed text only
 * - MEDIUM: Old session OR new device OR recent sensitive changes → Force Google re-auth, OTP if re-auth fails
 * - HIGH: New device AND new country OR very old session → Google re-auth + OTP required
 */

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

// Constants for risk thresholds
const SESSION_MEDIUM_RISK_HOURS = 12;
const SESSION_HIGH_RISK_HOURS = 24;
const ACCOUNT_LOW_TRUST_HOURS = 24;
const ACCOUNT_MEDIUM_TRUST_DAYS = 7;

// Local storage keys for device tracking
const DEVICE_FINGERPRINT_KEY = 'lucidDock_deviceFingerprint';
const LAST_AUTH_TIME_KEY = 'lucidDock_lastAuthTime';

export type RiskLevel = 'low' | 'medium' | 'high';

export interface RiskAssessment {
  level: RiskLevel;
  factors: RiskFactor[];
  requiresGoogleReauth: boolean;
  requiresOTP: boolean;
  message: string;
}

export interface RiskFactor {
  name: string;
  detected: boolean;
  severity: 'low' | 'medium' | 'high';
  description: string;
}

class DeletionRiskService {
  /**
   * Generate a simple device fingerprint (not for tracking, just for risk assessment)
   * Uses user agent + screen dimensions + timezone
   */
  private generateDeviceFingerprint(): string {
    const ua = navigator.userAgent;
    const screen = `${window.screen.width}x${window.screen.height}`;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const language = navigator.language;

    // Simple hash of these values
    const raw = `${ua}|${screen}|${timezone}|${language}`;
    let hash = 0;
    for (let i = 0; i < raw.length; i++) {
      const char = raw.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash.toString(36);
  }

  /**
   * Check if this is a known device for the user
   */
  private isKnownDevice(): boolean {
    const currentFingerprint = this.generateDeviceFingerprint();
    const storedFingerprint = localStorage.getItem(DEVICE_FINGERPRINT_KEY);

    // If no stored fingerprint, this is first time - store it and consider known
    if (!storedFingerprint) {
      localStorage.setItem(DEVICE_FINGERPRINT_KEY, currentFingerprint);
      return true;
    }

    return storedFingerprint === currentFingerprint;
  }

  /**
   * Record successful authentication time
   */
  recordAuthTime(): void {
    localStorage.setItem(LAST_AUTH_TIME_KEY, Date.now().toString());
    // Also update device fingerprint on successful auth
    localStorage.setItem(DEVICE_FINGERPRINT_KEY, this.generateDeviceFingerprint());
  }

  /**
   * Get hours since last authentication
   */
  private getSessionAgeHours(): number {
    const lastAuth = localStorage.getItem(LAST_AUTH_TIME_KEY);
    if (!lastAuth) {
      // No recorded auth time, assume old session
      return SESSION_HIGH_RISK_HOURS + 1;
    }

    const lastAuthTime = parseInt(lastAuth, 10);
    const now = Date.now();
    const hoursElapsed = (now - lastAuthTime) / (1000 * 60 * 60);
    return hoursElapsed;
  }

  /**
   * Get account age in hours
   */
  private async getAccountAgeHours(userId: string): Promise<number> {
    try {
      const pilotRef = doc(db, 'pilots', userId);
      const pilotDoc = await getDoc(pilotRef);

      if (!pilotDoc.exists()) {
        return ACCOUNT_MEDIUM_TRUST_DAYS * 24 + 1; // Default to medium trust if not found
      }

      const pilotData = pilotDoc.data();
      const createdAt = pilotData.createdAt;

      if (!createdAt) {
        return ACCOUNT_MEDIUM_TRUST_DAYS * 24 + 1;
      }

      // Handle both Timestamp and Date objects
      const createdDate = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
      const now = new Date();
      const hoursElapsed = (now.getTime() - createdDate.getTime()) / (1000 * 60 * 60);
      return hoursElapsed;
    } catch (error) {
      console.error('Error getting account age:', error);
      return ACCOUNT_MEDIUM_TRUST_DAYS * 24 + 1;
    }
  }

  /**
   * Assess deletion risk for a user
   */
  async assessDeletionRisk(userId: string): Promise<RiskAssessment> {
    const factors: RiskFactor[] = [];
    let riskScore = 0;

    // 1. Session age check (MOST IMPORTANT)
    const sessionAgeHours = this.getSessionAgeHours();
    if (sessionAgeHours > SESSION_HIGH_RISK_HOURS) {
      factors.push({
        name: 'Session Age',
        detected: true,
        severity: 'high',
        description: `Session is over ${SESSION_HIGH_RISK_HOURS} hours old`,
      });
      riskScore += 3;
    } else if (sessionAgeHours > SESSION_MEDIUM_RISK_HOURS) {
      factors.push({
        name: 'Session Age',
        detected: true,
        severity: 'medium',
        description: `Session is over ${SESSION_MEDIUM_RISK_HOURS} hours old`,
      });
      riskScore += 2;
    } else {
      factors.push({
        name: 'Session Age',
        detected: false,
        severity: 'low',
        description: 'Recent session',
      });
    }

    // 2. Device/browser check
    const knownDevice = this.isKnownDevice();
    if (!knownDevice) {
      factors.push({
        name: 'New Device',
        detected: true,
        severity: 'medium',
        description: 'This device has not been used before',
      });
      riskScore += 2;
    } else {
      factors.push({
        name: 'Known Device',
        detected: false,
        severity: 'low',
        description: 'Recognized device',
      });
    }

    // 3. Account age check
    const accountAgeHours = await this.getAccountAgeHours(userId);
    if (accountAgeHours < ACCOUNT_LOW_TRUST_HOURS) {
      factors.push({
        name: 'New Account',
        detected: true,
        severity: 'medium',
        description: 'Account created less than 24 hours ago',
      });
      riskScore += 2;
    } else if (accountAgeHours < ACCOUNT_MEDIUM_TRUST_DAYS * 24) {
      factors.push({
        name: 'Recent Account',
        detected: true,
        severity: 'low',
        description: 'Account created less than 7 days ago',
      });
      riskScore += 1;
    } else {
      factors.push({
        name: 'Established Account',
        detected: false,
        severity: 'low',
        description: 'Account is well established',
      });
    }

    // Determine risk level based on score and specific combinations
    let level: RiskLevel;
    let requiresGoogleReauth = true; // Always require re-auth
    let requiresOTP = false;
    let message = '';

    // HIGH RISK: New device AND (very old session OR new account)
    const isHighRisk =
      !knownDevice && (sessionAgeHours > SESSION_HIGH_RISK_HOURS || accountAgeHours < ACCOUNT_LOW_TRUST_HOURS);

    // MEDIUM RISK: Old session OR new device OR new account (any single factor)
    const isMediumRisk =
      sessionAgeHours > SESSION_MEDIUM_RISK_HOURS ||
      !knownDevice ||
      accountAgeHours < ACCOUNT_MEDIUM_TRUST_DAYS * 24;

    if (isHighRisk || riskScore >= 5) {
      level = 'high';
      requiresOTP = true;
      message = 'For your security, please verify with Google and enter the code sent to your email.';
    } else if (isMediumRisk || riskScore >= 2) {
      level = 'medium';
      requiresOTP = false; // OTP only if re-auth fails
      message = 'Please re-authenticate with Google to continue.';
    } else {
      level = 'low';
      requiresOTP = false;
      message = 'Please confirm to delete your account.';
    }

    return {
      level,
      factors,
      requiresGoogleReauth,
      requiresOTP,
      message,
    };
  }

  /**
   * Clear device fingerprint on logout
   */
  clearDeviceData(): void {
    localStorage.removeItem(DEVICE_FINGERPRINT_KEY);
    localStorage.removeItem(LAST_AUTH_TIME_KEY);
  }
}

export const deletionRiskService = new DeletionRiskService();
